import {startOfTomorrow} from 'date-fns';
import {
  Alert,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  FormField,
  Separator,
  showNotification,
  useFormContext,
  type FormControl,
  type FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useWatch} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {
  newVehicleApi,
  useApplyMidYearPriceAdjustmentMutation,
  useGetModelYearQuery,
  useListPricelistVersionHistoryQuery,
  type ModelYearResponseBody,
} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import {parseDate, type ApiError} from 'shared/web';

import {
  FormSection,
  handleCatalogueError,
  isStaleCatalogueVersion,
  optionalIsoDate,
  t,
  warnStaleCatalogueVersion,
} from './catalogueUi';
import {ImporterPriceListFields} from './ImporterPriceListFields';
import {IMPORTER_PRICE_LIST_FIELD_NAMES, importerPriceListSchema} from './importerPriceListSchema';
import {moneyInput} from './moneyInput';
import {NumberField} from './NumberField';
import {PriceChangesTable} from './PriceChangesTable';
import {toAdjustmentRequest} from './toAdjustmentRequest';
import {useAdjustableItems, type AdjustableItems} from './useAdjustableItems';

const SCOPES = ['all', 'trim', 'variant', 'colour', 'upholstery', 'package', 'feature'] as const;
type Scope = (typeof SCOPES)[number];
type PreviewItem = {
  resourceType: string;
  resourceId: string;
  name: string;
  before: string;
  after: string;
};

const adjustmentSchema = z
  .object({
    kind: z.enum(['percentage', 'amount']),
    direction: z.enum(['up', 'down']),
    value: moneyInput,
    scope: z.enum(SCOPES),
    resourceIds: z.array(z.string()),
    note: z.string(),
    nextVersion: z.string().trim().min(1),
    effectiveFrom: optionalIsoDate,
    updateImporterPriceList: z.boolean(),
    importerPriceList: importerPriceListSchema,
    /** The model year version `importerPriceList` was copied from when the update was switched on. */
    importerPriceListFromVersion: z.number(),
  })
  .superRefine((values, ctx) => {
    if (values.scope !== 'all' && values.resourceIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resourceIds'],
        message: t(
          'entity.newVehicle.catalogue.adjustment.pickItems',
          'Pick at least one item, or change the scope to the whole pricelist.'
        ),
      });
    }
  });
export type AdjustmentValues = z.infer<typeof adjustmentSchema>;

function AdjustmentFields(props: {
  control: FormControl<AdjustmentValues>;
  items: AdjustableItems;
  currentLabel: string;
  preview: {request: string; items: PreviewItem[]} | undefined;
  version: number;
  currency: string;
  modelYearId: string;
  canSchedule: boolean;
  pendingSchedule: {label: string; date: string} | undefined;
  modelYear: ModelYearResponseBody;
}) {
  const values = useWatch({control: props.control});
  const {setValue} = useFormContext<AdjustmentValues>();
  const parsed = adjustmentSchema.safeParse(values);
  const request = parsed.success
    ? JSON.stringify(toAdjustmentRequest(parsed.data, props.version))
    : undefined;
  const isPreviewCurrent = Boolean(props.preview && props.preview.request === request);
  const scope = (values.scope ?? 'all') as Scope;
  const isScheduled = Boolean(values.effectiveFrom);
  const previewCount = props.preview?.items.length ?? 0;
  const applyTitle = isScheduled
    ? t('entity.newVehicle.catalogue.adjustment.schedule', 'Schedule changes ({count})', {
        count: previewCount,
      })
    : t('entity.newVehicle.catalogue.adjustment.apply', 'Apply changes ({count})', {
        count: previewCount,
      });
  const applyHint = isScheduled
    ? t(
        'entity.newVehicle.catalogue.adjustment.scheduleHint',
        'Until the date, you can cancel the scheduled change in the pricelist version history.'
      )
    : t(
        'entity.newVehicle.catalogue.adjustment.applyHint',
        'Applying cannot be undone, but you can run another adjustment later.'
      );

  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.adjustment.change', 'Change')}>
        <Grid columns={3} spacing={3}>
          <FormField
            control={props.control}
            name="direction"
            type="choice"
            menuInPortal
            isNotClearable
            label={t('entity.newVehicle.catalogue.direction', 'Direction')}
            options={(['up', 'down'] as const).map((value) => ({
              value,
              label: getNewVehicleEnumLabel('direction', value, t),
            }))}
          />
          <FormField
            control={props.control}
            name="kind"
            type="choice"
            menuInPortal
            isNotClearable
            label={t('entity.newVehicle.catalogue.changeKind', 'Change kind')}
            options={(['percentage', 'amount'] as const).map((value) => ({
              value,
              label: getNewVehicleEnumLabel('changeKind', value, t),
            }))}
          />
          <NumberField
            control={props.control}
            name="value"
            isRequired
            label={
              values.kind === 'amount'
                ? `${t('general.labels.amount', 'Amount')} (${props.currency})`
                : `${t('general.labels.amount', 'Amount')} (%)`
            }
            placeholder={values.kind === 'amount' ? '500' : '3'}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.appliesTo', 'Applies to')}>
        <FormField
          control={props.control}
          name="scope"
          type="choice"
          menuInPortal
          isNotClearable
          label={t('entity.newVehicle.catalogue.scope', 'Scope')}
          helperText={match(scope)
            .with('all', () =>
              t(
                'entity.newVehicle.catalogue.adjustment.allHelp',
                'Every trim, variant, colour, upholstery and package price. Equipment prices are not changed.'
              )
            )
            .with('feature', () =>
              t(
                'entity.newVehicle.catalogue.adjustment.featureHelp',
                'The price of each picked item on every trim where it is Optional. Trims where it is free (0) keep 0.'
              )
            )
            .with('colour', 'package', () =>
              t(
                'entity.newVehicle.catalogue.adjustment.trimRowsHelp',
                'The price of each picked item on every trim it is offered on. Trims where it is included (0) keep 0.'
              )
            )
            .otherwise(() => undefined)}
          options={SCOPES.map((value) => ({
            value,
            label: getNewVehicleEnumLabel('adjustmentScope', value, t),
          }))}
        />
        <Show when={scope !== 'all'}>
          <FormField
            control={props.control}
            name="resourceIds"
            type="multiChoice"
            menuInPortal
            isRequired
            label={t('entity.newVehicle.catalogue.adjustment.items', 'Items to reprice')}
            options={scope === 'all' ? [] : props.items[scope]}
          />
        </Show>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.adjustment.version', 'New pricelist')}>
        <Grid columns={2} spacing={3}>
          <FormField
            control={props.control}
            name="nextVersion"
            type="text"
            isRequired
            label={t('entity.newVehicle.catalogue.nextPricelistVersion', 'Next pricelist version')}
            helperText={t(
              'entity.newVehicle.catalogue.adjustment.versionHelp',
              'Currently {label}. Must be a label this model year has not used before.',
              {label: props.currentLabel}
            )}
          />
          <FormField
            control={props.control}
            name="note"
            type="text"
            label={t('general.labels.note', 'Note')}
            placeholder={t(
              'entity.newVehicle.catalogue.adjustment.notePlaceholder',
              'e.g. Importer price increase Q3'
            )}
          />
        </Grid>
        <Show when={props.canSchedule}>
          <FormField
            control={props.control}
            name="effectiveFrom"
            type="apiDate"
            minDate={startOfTomorrow()}
            label={t('entity.newVehicle.catalogue.adjustment.effectiveFrom', 'Effective from')}
            helperText={t(
              'entity.newVehicle.catalogue.adjustment.effectiveFromHelp',
              'Leave empty to change prices now. With a date, the new prices apply from midnight on that day; until then the current prices stay in use.'
            )}
          />
        </Show>
      </FormSection>
      <Separator spacing={0} />
      <FormSection
        title={t('entity.newVehicle.catalogue.importerPriceList.title', 'Importer price list')}
      >
        <FormField
          control={props.control}
          name="updateImporterPriceList"
          type="checkbox"
          label={t(
            'entity.newVehicle.catalogue.importerPriceList.alsoUpdate',
            'Also update the importer price list'
          )}
          helperText={t(
            'entity.newVehicle.catalogue.importerPriceList.alsoUpdateHelp',
            'It changes together with the pricelist version, on the effective date when one is set.'
          )}
          // Hidden edits are still validated, so switching off drops them; switching on starts
          // from the section as last loaded.
          onChange={() => {
            setValue('importerPriceList', props.modelYear.importerPriceList);
            setValue('importerPriceListFromVersion', props.modelYear.version);
          }}
        />
        <Show when={values.updateImporterPriceList}>
          <ImporterPriceListFields
            control={props.control}
            names={IMPORTER_PRICE_LIST_FIELD_NAMES}
          />
        </Show>
      </FormSection>
      <Show when={props.pendingSchedule}>
        {(pendingSchedule) => (
          <Alert
            variant="warning"
            message={t(
              'entity.newVehicle.catalogue.adjustment.pendingSchedule',
              'Pricelist {label} is scheduled to take effect on {date}. Cancel it in the pricelist version history before making another adjustment.',
              pendingSchedule
            )}
          />
        )}
      </Show>
      <Show when={isPreviewCurrent}>
        <VStack spacing={2}>
          <Separator spacing={0} />
          <Text size="small" alternative>
            {t(
              'entity.newVehicle.catalogue.adjustment.previewTitle',
              'Prices that will change: {count}',
              {count: previewCount}
            )}
          </Text>
          <Box maxHeight={80} overflowY="auto">
            <PriceChangesTable
              currency={props.currency}
              modelYearId={props.modelYearId}
              changes={props.preview?.items ?? []}
            />
          </Box>
          <Alert
            variant="info"
            message={t(
              'entity.newVehicle.catalogue.adjustment.offersSafe',
              'Saved configurations and issued offers keep the prices they were created with.'
            )}
          />
        </VStack>
      </Show>
      <HStack justify="space-between" align="center" spacing={3}>
        <Text size="xSmall" color="secondary">
          {isPreviewCurrent
            ? applyHint
            : t(
                'entity.newVehicle.catalogue.adjustment.previewHint',
                'Preview the new prices before anything is saved.'
              )}
        </Text>
        <FormButton
          control={props.control}
          type="submit"
          variant={isPreviewCurrent ? 'primary' : 'secondary'}
          isDisabled={Boolean(props.pendingSchedule)}
          title={
            isPreviewCurrent
              ? applyTitle
              : t('entity.newVehicle.catalogue.adjustment.preview', 'Preview changes')
          }
        />
      </HStack>
    </VStack>
  );
}

/**
 * Reprices part or all of a model year and moves it to a new pricelist version. The same endpoint
 * previews with `previewOnly`, so the dialog always shows the exact before/after list first and
 * only applies the request that preview was made for.
 */
export function PriceAdjustmentDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  modelYearId: string;
  modelYearVersion: number;
  pricelistVersionLabel: string;
  currency: string;
  canSchedule: boolean;
}) {
  const dispatch = useDispatch();
  const adjustable = useAdjustableItems(props.modelYearId, {skip: !props.isOpen});
  // Refetched on every open: a cached schedule may have taken effect or been made by someone else.
  const history = useListPricelistVersionHistoryQuery(
    {modelYearId: props.modelYearId},
    {skip: !props.isOpen, refetchOnMountOrArgChange: true}
  );
  const year = useGetModelYearQuery(
    {modelYearId: props.modelYearId},
    {skip: !props.isOpen, refetchOnMountOrArgChange: true}
  );
  const formatDate = useDateTimeFormatter();
  const formatApiDate = (value: string) => formatDate('dateShort', parseDate(value));
  const scheduledVersion = history.data?.items.find((item) => item.activatedAt === null);
  const pendingSchedule = scheduledVersion && {
    label: scheduledVersion.versionLabel,
    date: formatApiDate(scheduledVersion.effectiveFrom),
  };
  const [adjustPrices] = useApplyMidYearPriceAdjustmentMutation();
  const [preview, setPreview] = useState<{request: string; items: PreviewItem[]}>();
  const close = () => {
    setPreview(undefined);
    props.onClose();
  };

  const reloadCatalogue = () =>
    dispatch(
      newVehicleApi.util.invalidateTags(['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'])
    );

  const submit: FormSubmitHandler<AdjustmentValues> = async (values, setErrors) => {
    /**
     * The year may reload after the section was copied; sent on the newer version, it would
     * overwrite another user's section unseen. Refused like a stale version, before any request.
     */
    if (
      values.updateImporterPriceList &&
      values.importerPriceListFromVersion !== props.modelYearVersion
    ) {
      warnStaleCatalogueVersion();
      reloadCatalogue();
      close();
      return;
    }
    /**
     * A stale model-year version reloads the catalogue so the next try sends the fresh one. With the
     * importer price list it also closes, as the editors do: the section may have changed meanwhile.
     */
    const handleRefusal = (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (isStaleCatalogueVersion(error)) {
        reloadCatalogue();
        if (values.updateImporterPriceList) {
          close();
        }
      }
    };
    const request = toAdjustmentRequest(values, props.modelYearVersion);
    const signature = JSON.stringify(request);
    if (preview?.request !== signature) {
      await adjustPrices({
        modelYearId: props.modelYearId,
        priceAdjustmentRequestBody: {...request, previewOnly: true},
      })
        .unwrap()
        .then((response) => setPreview({request: signature, items: response.preview}))
        .catch(handleRefusal);
      return;
    }
    await adjustPrices({
      modelYearId: props.modelYearId,
      priceAdjustmentRequestBody: {
        ...request,
        // Not part of the preview: the section changes no price.
        ...(values.updateImporterPriceList && {importerPriceList: values.importerPriceList}),
        previewOnly: false,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(
          request.effectiveFrom
            ? t(
                'entity.newVehicle.catalogue.adjustment.scheduled',
                'Prices change to {label} on {date}.',
                {
                  label: request.nextPricelistVersionLabel,
                  date: formatApiDate(request.effectiveFrom),
                }
              )
            : t(
                'entity.newVehicle.catalogue.adjustment.applied',
                'Prices updated. Pricelist is now {label}.',
                {label: request.nextPricelistVersionLabel}
              )
        );
        close();
      })
      .catch(handleRefusal);
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={close}
      size="large"
      title={t('entity.newVehicle.catalogue.midYearAdjustment', 'Mid-year price adjustment')}
    >
      <DataStatus
        isLoading={adjustable.isLoading || history.isLoading || year.isLoading}
        isError={year.isError}
      >
        <Show when={year.data}>
          {(modelYear) => (
            <Form<AdjustmentValues>
              experimentalZodSchema={adjustmentSchema}
              defaultValues={{
                kind: 'percentage',
                direction: 'up',
                value: '',
                scope: 'all',
                resourceIds: [],
                note: '',
                nextVersion: '',
                effectiveFrom: null,
                updateImporterPriceList: false,
                importerPriceList: modelYear.importerPriceList,
                importerPriceListFromVersion: modelYear.version,
              }}
              onSubmit={submit}
            >
              {(control) => (
                <AdjustmentFields
                  control={control}
                  items={adjustable.items}
                  currentLabel={props.pricelistVersionLabel}
                  preview={preview}
                  version={props.modelYearVersion}
                  currency={props.currency}
                  modelYearId={props.modelYearId}
                  canSchedule={props.canSchedule}
                  pendingSchedule={pendingSchedule}
                  modelYear={modelYear}
                />
              )}
            </Form>
          )}
        </Show>
      </DataStatus>
    </Dialog>
  );
}
